import app from 'flarum/forum/app';
import FormModal from 'flarum/common/components/FormModal';
import Button from 'flarum/common/components/Button';
import Form from 'flarum/common/components/Form';
import Select from 'flarum/common/components/Select';
import Stream from 'flarum/common/utils/Stream';
import extractText from 'flarum/common/utils/extractText';
import { discussionTypeOptions, defaultDiscussionType } from '../discussionTypeOptions';

/**
 * Modal to edit a discussion's BHIC metadata (Sanity ID, discussion type and
 * Memorix ID) after creation. Opened from the discussion moderation controls;
 * gated on `canManageMetadataForDiscussion` by the caller. Saves via
 * `discussion.save()` → PATCH /api/discussions/{id}, which the backend
 * authorizes on the `bhic.discussion.manageMetadata` permission.
 */
export default class EditDiscussionMetadataModal extends FormModal {
  oninit(vnode) {
    super.oninit(vnode);
    this.discussion = this.attrs.discussion;
    this.sanityId = Stream(this.discussion.sanityId() || '');
    this.discussionType = Stream(this.discussion.discussionType() || defaultDiscussionType());
    this.memorixId = Stream(this.discussion.memorixId() || '');
  }
  className() {
    return 'EditDiscussionMetadataModal Modal--small';
  }
  title() {
    return app.translator.trans('bhic.forum.edit_metadata.title');
  }
  content() {
    return m("div", {
      className: "Modal-body"
    }, m(Form, {
      className: "Form--centered"
    }, m("div", {
      className: "Form-group"
    }, m("label", null, app.translator.trans('bhic.forum.composer.discussion_type_label')), m(Select, {
      value: this.discussionType(),
      options: discussionTypeOptions(),
      onchange: this.discussionType
    })), m("div", {
      className: "Form-group"
    }, m("label", null, app.translator.trans('bhic.forum.composer.sanity_id_label')), m("input", {
      className: "FormControl",
      bidi: this.sanityId,
      type: "text",
      maxlength: 255,
      placeholder: extractText(app.translator.trans('bhic.forum.composer.sanity_id_placeholder'))
    })), m("div", {
      className: "Form-group"
    }, m("label", null, app.translator.trans('bhic.forum.composer.memorix_id_label')), m("input", {
      className: "FormControl",
      bidi: this.memorixId,
      type: "text",
      maxlength: 255,
      placeholder: extractText(app.translator.trans('bhic.forum.composer.memorix_id_placeholder'))
    })), m("div", {
      className: "Form-group Form-controls"
    }, m(Button, {
      className: "Button Button--primary Button--block",
      type: "submit",
      loading: this.loading
    }, app.translator.trans('bhic.forum.edit_metadata.submit_button')))));
  }
  onsubmit(e) {
    e.preventDefault();

    // Send null for cleared values so they reset to NULL (and never collide on
    // the unique indexes). Only include fields that actually changed.
    const attributes = {};
    this.collectChange(attributes, 'sanityId', this.sanityId().trim());
    this.collectChange(attributes, 'discussionType', this.discussionType());
    this.collectChange(attributes, 'memorixId', this.memorixId().trim());
    if (Object.keys(attributes).length === 0) {
      this.hide();
      return;
    }
    this.loading = true;
    return this.discussion.save(attributes).then(() => {
      m.redraw();
      this.hide();
    }).catch(() => {
      this.loading = false;
      m.redraw();
    });
  }
  collectChange(attributes, key, value) {
    const current = this.discussion[key]() || '';
    if (value !== current) {
      attributes[key] = value === '' ? null : value;
    }
  }
}
flarum.reg.add('flarum-flarum', 'forum/components/EditDiscussionMetadataModal', EditDiscussionMetadataModal);